/**
 * Users feature — Zod schemas + inferred types.
 *
 * Users are tenant-scoped. Every endpoint takes the tenant slug
 * and returns records that belong only to that tenant. The admin
 * API never returns users across tenant boundaries in a single
 * response; if you need that, call the endpoint once per tenant.
 *
 * Responses the contract publishes (user refs, push devices, device identities, containment)
 * are generated into ./admin.generated.ts and re-exported here.
 */

import { z } from "zod";
import {
  containAuthorityResponseSchema,
  devicePostureVerdictSchema,
  userDeviceIdentitiesResponseSchema,
  userDeviceIdentitySchema,
  userObservedDeviceSchema,
  userPushTokenSchema,
  userPushTokensResponseSchema,
  userRefSchema,
  userRefsResponseSchema,
} from "./admin.generated";

export const personTabSchema = z.enum(["attributes", "passkeys", "device-identities", "sessions", "devices", "access-passes", "recovery", "frontline", "agent-activity"]);
export const userDetailSearchSchema = z.object({
  tab: personTabSchema.optional().catch(undefined),
  import_batch: z.string().uuid().optional(),
});

// ── Enums ─────────────────────────────────────────────────────────────────
//
// The shapes in this section mirror backend/pkg/api/identity/user.go
// exactly. Drift here means every users-list response Zod-fails and
// the page renders an error card. When the backend changes one of
// these enums, update both sides in lockstep.

// users.status CHECK on the database matches user.Status in
// backend/internal/identity/user/user.go.
export const userStatusSchema = z.enum([
  "active",
  "suspended",
  "deactivated",
  "deleted",
]);
export type UserStatus = z.infer<typeof userStatusSchema>;

// user.Source values written by every code path that creates a
// users row. There is no "local" / "ldap" / "google" / "azure_ad"
// in the backend — those would be source attribution for an IdP
// federation surface, not the canonical provisioning source.
//
// MUST stay in lockstep with backend/internal/identity/user/user.go's
// Source constants and the users_source_check CHECK constraint. This is a
// closed enum on a READ path, so a value the backend can write and this
// list omits does not degrade — it throws, and the whole users list fails
// to render for every operator the moment one such user exists. That has
// already happened twice: `federation` and `gatekeeper` shipped backend-side
// without landing here, and `directory_sync` (AD inbound) made it visible.
export const userSourceSchema = z.enum([
  "admin_created",
  "invitation",
  "scim_provisioned",
  "self_registered",
  "federation",
  "gatekeeper",
  "directory_sync",
]);
export type UserSource = z.infer<typeof userSourceSchema>;

// ── List filter (URL search params) ───────────────────────────────────────
//
// The URL surface is intentionally richer than what the backend
// list endpoint accepts today (q / status / source / sortBy are
// not yet wired through the backend). The page filters client-side
// for now; backend support can grow later without breaking URLs.

export const usersListSearchSchema = z.object({
  q: z.string().optional(),
  /** One organization's people (the organization page's Members tab opens its own). */
  organization_id: z.string().uuid().optional(),
  status: userStatusSchema.optional(),
  source: userSourceSchema.optional(),
  sortBy: z.enum(["email", "display_name", "created_at", "last_login_at"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  // Keyset pagination (browse mode only). The backend `GET /users` browse path
  // is cursor-keyed, not offset-based, and returns no total — so page N is not
  // seekable. We keep the stack of cursors followed to reach the current page
  // (absent = page 1; the current page's cursor is the last element), which is
  // what makes an explicit Prev/Next pager work while staying URL-addressable.
  // Ignored in search mode (?q=), which the backend returns as a non-paginated
  // relevance-ranked top-N.
  cursors: z.array(z.string()).optional(),
});
export type UsersListSearch = z.infer<typeof usersListSearchSchema>;

// ── List row ──────────────────────────────────────────────────────────────
//
// Matches apiidentity.UserResponse on the backend. Optional fields
// are `omitempty` on the wire, so they arrive as `undefined` (NOT
// null). The `email` accessor at the table level reads
// `primary_email` directly — that's the canonical wire field.

export const userListItemSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  display_name: z.string(),
  primary_email: z.string().optional(),
  primary_phone: z.string().optional(),
  status: userStatusSchema,
  source: userSourceSchema,
  external_id: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  last_login_at: z.string().optional(),
});
export type UserListItem = z.infer<typeof userListItemSchema>;

export const usersListResponseSchema = z.object({
  items: z.array(userListItemSchema),
  // Browse mode (no query): keyset cursor. Search mode (query present): relevance-ranked top-N with
  // an exact total + has_more truncation signal ("top N of M — refine"), and no cursor.
  next_cursor: z.string().optional(),
  total: z.number().optional(),
  has_more: z.boolean().optional(),
});
export type UsersListResponse = z.infer<typeof usersListResponseSchema>;

// One resolved display ref from the batch id-resolve endpoint (POST /users/resolve) — the minimal
// label projection a picker/table needs for an id it already holds. Only ids that resolve within the
// tenant come back; the caller renders any missing id as a short id (never a fabricated label).
export { userRefSchema, userRefsResponseSchema };
export type UserRef = z.infer<typeof userRefSchema>;

export type UserRefsResponse = z.infer<typeof userRefsResponseSchema>;

// ── Detail + sub-resources ────────────────────────────────────────────────
//
// UserResponse is the same shape for list and detail on the backend
// — there is no separate detail surface. We declare a wider object
// here for the detail page so the UI can still rely on a
// reasonable set of typed fields.

export const userDetailSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  organization_id: z.string().optional(),

  // Structured name (RFC 7643 §4.1.1). Only display_name is required.
  display_name: z.string(),
  given_name: z.string().optional(),
  family_name: z.string().optional(),
  middle_name: z.string().optional(),
  nickname: z.string().optional(),

  primary_email: z.string().optional(),
  primary_phone: z.string().optional(),

  status: userStatusSchema,
  source: userSourceSchema,
  locale: z.string().optional(),
  timezone: z.string().optional(),
  external_id: z.string().optional(),
  external_source: z.string().optional(),
  // The identity source that brought this person (People → Connections →
  // Sources); absent when none is recorded.
  identity_source_id: z.string().optional(),

  // Profile (RFC 7643 §4.1).
  title: z.string().optional(),
  profile_url: z.string().optional(),
  user_type: z.string().optional(),

  // Enterprise User extension (RFC 7643 §4.3). `ent_organization` is the
  // UPSTREAM directory's organization string — not `organization_id`, which is
  // the B2B2C organization row.
  department: z.string().optional(),
  division: z.string().optional(),
  employee_number: z.string().optional(),
  cost_center: z.string().optional(),
  ent_organization: z.string().optional(),
  manager_value: z.string().optional(),
  manager_display_name: z.string().optional(),

  // Recovery-email STATE, never the address — the backend deliberately does not
  // send it (see UserResponse in backend/pkg/api/identity/user.go). Both are
  // plain booleans the server always emits, so neither is optional.
  recovery_email_set: z.boolean(),
  recovery_email_verified: z.boolean(),

  // Active Directory identity attributes. Ordinary free-text attributes — the
  // inbound directory sync is the usual writer, but they are also settable
  // through the admin user API (a tenant with no AD still needs
  // `ad_object_guid` to federate to Office 365). Not SCIM attributes.
  //
  // `ad_object_guid` is conventionally base64 of AD's raw 16 bytes — the Office
  // 365 / Entra ImmutableID. Treat it as OPAQUE: never parse or re-encode it
  // client-side, because objectGUID's display form is mixed-endian and a round
  // trip through a UUID parser yields an ImmutableID that matches nobody.
  // `ad_object_guid_display` is the server-derived readable rendering, present
  // only when the stored value is in that encoding, for display only — it is
  // never sent back.
  ad_dn: z.string().optional(),
  ad_object_sid: z.string().optional(),
  ad_object_guid: z.string().optional(),
  ad_object_guid_display: z.string().optional(),
  ad_user_principal_name: z.string().optional(),

  version: z.number().int().nonnegative().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  scim_updated_at: z.string().optional(),
  last_login_at: z.string().optional(),
  deactivated_at: z.string().optional(),
});
export type UserDetail = z.infer<typeof userDetailSchema>;

export const userPasskeySchema = z.object({
  id: z.string(),
  // These map to backend `*string` fields with `omitempty`: when unset the
  // key is omitted from the JSON entirely (arrives as undefined, not null),
  // so they must be nullish — not merely nullable — or an unnamed / never-used
  // passkey fails to parse. (The backend never emits an explicit null here.)
  friendly_name: z.string().nullish(),
  aaguid: z.string().nullish(),
  authenticator_name: z.string().nullish(),
  transports: z.array(z.string()).default([]),
  created_at: z.string(),
  last_used_at: z.string().nullish(),
  backup_eligible: z.boolean(),
  backup_state: z.boolean(),
  /** Server-computed kind (docs/security.md); the per-kind caps count by it. */
  kind: z.enum(["workstation_logon", "security_key", "synced", "device_bound"]),
});
export type UserPasskey = z.infer<typeof userPasskeySchema>;

/** Headroom under one passkey cap. limit 0 = unlimited. */
export const limitUsageSchema = z.object({
  used: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
});
export type LimitUsage = z.infer<typeof limitUsageSchema>;

/**
 * Per-cap headroom the credentials list reports (docs/security.md). Optional on the
 * wire: a server with no caps policy omits it, and absent means unknown.
 */
export const passkeyLimitsSchema = z.object({
  total: limitUsageSchema,
  by_kind: z.record(z.string(), limitUsageSchema),
});
export type PasskeyLimits = z.infer<typeof passkeyLimitsSchema>;

export const userPasskeysResponseSchema = z.object({
  items: z.array(userPasskeySchema),
  limits: passkeyLimitsSchema.optional(),
});
export type UserPasskeysResponse = z.infer<typeof userPasskeysResponseSchema>;

export const userSessionSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
  last_active_at: z.string().nullish(),
  user_agent: z.string().nullish(),
  ip_address: z.string().nullish(),
  revoked_at: z.string().nullish(),
});
export type UserSession = z.infer<typeof userSessionSchema>;

/**
 * A registered mobile-authenticator install — an FCM push target.
 *
 * The raw fcm_token is NEVER returned by the API (it is a delivery secret
 * written only by the client), so there is deliberately no field for it here.
 */
export { userPushTokenSchema, userPushTokensResponseSchema };
export type UserPushToken = z.infer<typeof userPushTokenSchema>;

// Existing workstation_user_logon attribution projected onto the user's
// Devices tab. This represents PureID certificate issuance, not LSA success.
export const userWindowsWorkstationSchema = z.object({
  device_id: z.string(),
  jkt: z.string(),
  hostname_reported: z.string().optional(),
  status: z.string(),
  last_authentication_at: z.string(),
});
export type UserWindowsWorkstation = z.infer<typeof userWindowsWorkstationSchema>;

/** A browser or client PureID observed at this person's sign-in (contract-generated). */
export type UserObservedDevice = z.infer<typeof userObservedDeviceSchema>;

export const userDevicesResponseSchema = z.object({
  items: z.array(userObservedDeviceSchema),
  windows_workstations: z.array(userWindowsWorkstationSchema).default([]),
});
export type UserDevices = z.infer<typeof userDevicesResponseSchema>;

// ── Mutation inputs ───────────────────────────────────────────────────────

export const updateUserStatusSchema = z.object({
  status: userStatusSchema,
});
export type UpdateUserStatusInput = z.infer<typeof updateUserStatusSchema>;

// ── Attribute edit (PATCH /users/{id}) ─────────────────────────────────────
//
// The attributes an administrator may write, grouped the way the Attributes
// panel shows them. The server decides writability from its attribute
// registry; this list is the form's shape, not a second decision, and the
// server drops anything it does not accept. Metadata is not edited here: it is
// structured data a form of text fields cannot represent. The manager is not
// editable at all: approval and recovery read it as a separation-of-duties
// block, so no PureID surface writes it.

export const EDITABLE_USER_ATTRIBUTES = [
  "display_name",
  "given_name",
  "family_name",
  "middle_name",
  "nickname",
  "title",
  "user_type",
  "locale",
  "timezone",
  "profile_url",
  "department",
  "division",
  "employee_number",
  "cost_center",
  "ent_organization",
  "ad_dn",
  "ad_object_sid",
  "ad_object_guid",
  "ad_user_principal_name",
] as const;
export type EditableUserAttribute = (typeof EDITABLE_USER_ATTRIBUTES)[number];

export const userAttributesFormSchema = z.object({
  display_name: z.string().trim().min(1, "A display name is required").max(255, "At most 255 characters"),
  given_name: z.string(),
  family_name: z.string(),
  middle_name: z.string(),
  nickname: z.string(),
  title: z.string(),
  user_type: z.string(),
  locale: z.string().trim().min(1, "Every person has a locale"),
  timezone: z.string(),
  profile_url: z.string(),
  department: z.string(),
  division: z.string(),
  employee_number: z.string(),
  cost_center: z.string(),
  ent_organization: z.string(),
  ad_dn: z.string(),
  ad_object_sid: z.string(),
  ad_object_guid: z.string(),
  ad_user_principal_name: z.string(),
}) satisfies z.ZodType<Record<EditableUserAttribute, string>>;
export type UserAttributesFormValues = z.infer<typeof userAttributesFormSchema>;

// ── Create (POST /users) ──────────────────────────────────────────────────
//
// A person carries no credential when created: they enroll through a temporary
// access pass issued on their record.

export const createUserFormSchema = z.object({
  display_name: z.string().trim().min(1, "A display name is required").max(255, "At most 255 characters"),
  email: z.string().trim().email("Enter an email address"),
  given_name: z.string().trim(),
  family_name: z.string().trim(),
});
export type CreateUserFormValues = z.infer<typeof createUserFormSchema>;

// ── Adoption (GET /users/{id}/adoption) ───────────────────────────────────
//
// Whether an upstream may adopt a person PureID already holds. review_required
// is the server's decision; the facts are why.

export const userAdoptionSchema = z.object({
  review_required: z.boolean(),
  active_credential_ids: z.array(z.string()),
  open_passes: z.number().int().nonnegative(),
  // A pass the person has already redeemed and not finished enrolling with:
  // a credential that can still be minted, which is why it blocks approval
  // just as an open pass does.
  in_flight_enrollments: z.number().int().nonnegative(),
  // A TOTP enrolment or unused recovery codes: another way into the account
  // that an approval cannot cover, so it blocks approval like an open pass.
  second_factors: z.number().int().nonnegative(),
  has_external_id: z.boolean(),
  directory_linked: z.boolean(),
  verified_email: z.boolean(),
  approved_at: z.string().nullable(),
  approved_by: z.string().nullable(),
});
export type UserAdoption = z.infer<typeof userAdoptionSchema>;

// Response of contain-authority (POST /users/{id}/contain-authority): the per-leg counts of the
// operator-initiated owner-offboard cascade — what one action actually did, never a bare "ok"
// (the composed kill's partial-is-partial discipline). A partial run answers 502 with the same
// body: `partial` and `failed_legs` name what did not complete.
export { containAuthorityResponseSchema };
export type ContainAuthorityResponse = z.infer<typeof containAuthorityResponseSchema>;

// A person's device identity (registry kind `self`, ruling R4): the machine
// principal their device acts through. Read-only on this surface — minting is
// the person's own passkey ceremony, and removal is the containment cascade.
/**
 * The newest believed posture verdict about a device, posted by the tenant's own EDR, MDM or posture
 * provider (private-access ruling R7). Absent means no verdict is believed right now — which the
 * private door reads as "no verdict", never as allow.
 */
export { devicePostureVerdictSchema, userDeviceIdentitySchema, userDeviceIdentitiesResponseSchema };
export type DevicePostureVerdict = z.infer<typeof devicePostureVerdictSchema>;
export type UserDeviceIdentity = z.infer<typeof userDeviceIdentitySchema>;
